import { FC, Dispatch, SetStateAction } from 'react'
import { InputTextType } from '../../../../../common/InputType'
import PhoneLabelLine from './PhoneLabelLine'
import { useLabelPopup } from '../../../../../hooks/useLabelPopup'
import { ExternalNumber } from '../../../../../common/ContactAddress.type'
import { MdDelete } from 'react-icons/md'
import { getUiLabelItems } from '../../../../../common/PhoneLabelItems'
import { CONTACT_FIELD_MAX_LENGTH } from '../../../../../util/contactFieldLimits'

type Props = {
  externalNumbers: ExternalNumber[]
  addExternalNumber: () => void
  updateExternalNumber: (
    index: number,
    partial: Partial<ExternalNumber>
  ) => void
  removeExternalNumber: (index: number) => void

  showLabelListIndex: number | null
  setShowLabelListIndex: Dispatch<SetStateAction<number | null>>
}

const MAX_ROWS = 5

const ContactPhoneLabel: FC<Props> = ({
  
  externalNumbers,
  addExternalNumber,
  updateExternalNumber,
  removeExternalNumber,
  showLabelListIndex,
  setShowLabelListIndex,
}) => {
  const popup = useLabelPopup({
    showLabelListIndex,
    setShowLabelListIndex,
  })

  const uiLabelItems = getUiLabelItems()
  const labelTextList = uiLabelItems.map((i) => i.label)

  return (
    <div className="position-relative">
      {externalNumbers.map((row, index) => {
        /* ---------- phone ---------- */
        const phoneInput: InputTextType = {
          type: 'tel',
          id: `phone_${index}`,
          label: index === 0 ? '電話' : '',
          placeholder: '',
          required: false,
          autoComplete: 'tel',
          state: row.externalPhoneNumber,
          setState: (value) => {
            const next =
              typeof value === 'function'
                ? value(row.externalPhoneNumber)
                : value

            updateExternalNumber(index, {
              externalPhoneNumber: next,
            })
          },
          readonly: false,
          maxLength: CONTACT_FIELD_MAX_LENGTH.externalPhoneNumber,
        }

        /* ---------- label input ---------- */
        const labelValue =
          row.labelCode === 99
            ? row.customLabel
            : uiLabelItems.find((i) => i.code === row.labelCode)?.label ?? ''

        const labelInput: InputTextType = {
          type: 'text',
          id: `label_${index}`,
          label: index === 0 ? 'ラベル' : '',
          placeholder: '',
          required: false,
          autoComplete: 'off',
          state: labelValue,
          setState: (value) => {
            const next =
              typeof value === 'function' ? value(labelValue) : value

            if ((next ?? '').trim() === '') {
              updateExternalNumber(index, {
                labelCode: 9,
                customLabel: '',
              })
              return
            }

            const matched = uiLabelItems.find((item) => item.label === next)

            if (matched) {
              updateExternalNumber(index, {
                labelCode: matched.code,
                customLabel: '',
              })
            } else {
              updateExternalNumber(index, {
                labelCode: 99,
                customLabel: next,
              })
            }
          },
          readonly: false,
          maxLength: CONTACT_FIELD_MAX_LENGTH.customLabel,
        }

        const canRemoveThisRow = externalNumbers.length > 1

        return (
          <div key={index} className={index === 0 ? '' : 'mt-3'}>
            <PhoneLabelLine
              rowIndex={index}
              phoneInput={phoneInput}
              labelInput={labelInput}
              showLabelListIndex={showLabelListIndex}
              defaultLabelList={labelTextList}
              setRowRef={popup.setRowRef}
              setLabelInputRef={popup.setLabelInputRef}
              onClickLabelInput={() => popup.open(index)}
              onFocusLabelInput={() => popup.onFocusLabel(index)}
              onBlurLabelInput={popup.onBlurLabel}
              onPopupMouseDown={popup.onPopupMouseDown}
              onClosePopup={popup.close}
              onSelectLabel={(selectedLabel) => {
                const matched = uiLabelItems.find(
                  (item) => item.label === selectedLabel,
                )

                updateExternalNumber(index, {
                  labelCode: matched?.code ?? 99,
                  customLabel: matched ? '' : selectedLabel,
                })

                popup.blurLabelInput(index)
              }}
              popupClassName="defaultLabelPopup--right"
              removeSlot={
                canRemoveThisRow ? (
                  <button
                    type="button"
                    className="phoneLabelRowRemove btn btn-sm"
                    onClick={() => {
                      removeExternalNumber(index)
                      popup.close()
                    }}
                  >
                    <MdDelete size={'1.4rem'} />
                  </button>
                ) : null
              }
            />
          </div>
        )
      })}

      <div className="mt-3 phoneLabelAddWrap">
        <button
          type="button"
          className="btn btn-light phoneLabelAddButton"
          onClick={addExternalNumber}
          disabled={externalNumbers.length >= MAX_ROWS}
        >
          + 電話番号追加
        </button>
      </div>
    </div>
  )
}

export default ContactPhoneLabel
